import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent } from './ui/card';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';
import { toast } from 'sonner';

const Contact = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const { lang } = useParams();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: '',
  });
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate checkboxes
    if (!acceptTerms || !acceptPrivacy) {
      toast.error(
        language === 'ar' 
          ? 'يرجى الموافقة على الشروط وسياسة الخصوصية' 
          : 'Please accept the terms and privacy policy',
        {
          duration: 3000,
        }
      );
      return;
    }

    setIsSubmitting(true);

    try {
      // Get current date
      const submissionDate = new Date().toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });

      console.log('Sending email with:', {
        service_id: process.env.REACT_APP_SERVICE_ID,
        template_id: process.env.REACT_APP_TEMPLATE_ID,
        user_id: process.env.REACT_APP_PUBLIC_KEY,
      });

      // Using EmailJS - Free email service
      const response = await fetch('https://api.emailjs.com/api/v1.0/email/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          service_id: process.env.REACT_APP_SERVICE_ID,
          template_id: process.env.REACT_APP_TEMPLATE_ID,
          user_id: process.env.REACT_APP_PUBLIC_KEY,
          template_params: {
            from_name: formData.name,
            from_email: formData.email,
            phone: formData.phone || 'Not provided',
            company: formData.company || 'Not provided',
            service: formData.service || 'General Inquiry',
            message: formData.message,
            submission_date: submissionDate,
          },
        }),
      });

      const responseText = await response.text();
      console.log('Response status:', response.status);
      console.log('Response text:', responseText);

      if (response.ok) {
        toast.success(t.contact.form.success || 'Message sent successfully!', {
          description: language === 'ar' ? 'سنتواصل معك قريباً!' : 'We will get back to you soon!',
          duration: 5000,
        });
        
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          service: '',
          message: '',
        });
        setAcceptTerms(false);
        setAcceptPrivacy(false);
      } else {
        throw new Error(responseText || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(
        language === 'ar' ? 'فشل إرسال الرسالة' : 'Failed to send message',
        {
          description: error.message || (language === 'ar' ? 'يرجى المحاولة مرة أخرى أو الاتصال بنا مباشرة.' : 'Please try again or contact us directly.'),
          duration: 5000,
        }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-br from-gray-50 to-teal-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 animate-fade-in-up">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            {t.contact.title}
          </h2>
          <p className="text-xl text-gray-600">{t.contact.subtitle}</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Info Cards */}
          <div className="space-y-6">
            {[
              { icon: Mail, label: t.contact.info.email, value: t.contact.info.email },
              { icon: Phone, label: t.contact.info.phone, value: t.contact.info.phone },
              { icon: MapPin, label: t.contact.info.address, value: t.contact.info.address },
              { icon: Clock, label: t.contact.info.hours, value: t.contact.info.hours },
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <Card
                  key={index}
                  className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-2 hover:border-teal-500"
                >
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 mb-1"  dir='ltr'>{item.label}</div>
                      <div className="text-gray-600 text-sm"  dir='ltr'>{item.value}</div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Contact Form */}
          <Card className="lg:col-span-2 border-2 shadow-xl">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">
                      {t.contact.form.name} *
                    </label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="border-2 focus:border-teal-500 transition-colors"
                      placeholder={t.contact.form.name}
                      dir={language === 'ar' ? 'rtl' : 'ltr'}
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">
                      {t.contact.form.email} *
                    </label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="border-2 focus:border-teal-500 transition-colors"
                      placeholder={t.contact.form.email}
                      dir="ltr"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">
                      {t.contact.form.phone}
                    </label>
                    <Input
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="border-2 focus:border-teal-500 transition-colors"
                      placeholder={t.contact.form.phone}
                      dir="ltr"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">
                      {t.contact.form.company}
                    </label>
                    <Input
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="border-2 focus:border-teal-500 transition-colors"
                      placeholder={t.contact.form.company}
                      dir={language === 'ar' ? 'rtl' : 'ltr'}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700">
                    {t.contact.form.service}
                  </label>
                  <Input
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className="border-2 focus:border-teal-500 transition-colors"
                    placeholder={t.contact.form.service}
                    dir={language === 'ar' ? 'rtl' : 'ltr'}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700">
                    {t.contact.form.message} *
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="border-2 focus:border-teal-500 transition-colors resize-none"
                    placeholder={t.contact.form.message}
                    dir={language === 'ar' ? 'rtl' : 'ltr'}
                  />
                </div>

                {/* Terms and Privacy Checkboxes */}
                <div className="space-y-3 pt-2">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="acceptTerms"
                      checked={acceptTerms}
                      onChange={(e) => setAcceptTerms(e.target.checked)}
                      className="mt-1 w-5 h-5 text-teal-600 border-2 border-gray-300 rounded focus:ring-2 focus:ring-teal-200 cursor-pointer"
                      required
                    />
                    <label htmlFor="acceptTerms" className="text-sm text-gray-700 cursor-pointer" dir={language === 'ar' ? 'rtl' : 'ltr'}>
                      {language === 'ar' ? (
                        <>
                          أوافق على{' '}
                          <button
                            type="button"
                            onClick={() => navigate(`/${lang || 'ar'}/terms-of-service`)}
                            className="text-teal-600 hover:text-teal-700 underline font-semibold"
                          >
                            شروط الخدمة
                          </button>
                        </>
                      ) : (
                        <>
                          I agree to the{' '}
                          <button
                            type="button"
                            onClick={() => navigate(`/${lang || 'en'}/terms-of-service`)}
                            className="text-teal-600 hover:text-teal-700 underline font-semibold"
                          >
                            Terms of Service
                          </button>
                        </>
                      )}
                    </label>
                  </div>

                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="acceptPrivacy"
                      checked={acceptPrivacy}
                      onChange={(e) => setAcceptPrivacy(e.target.checked)}
                      className="mt-1 w-5 h-5 text-teal-600 border-2 border-gray-300 rounded focus:ring-2 focus:ring-teal-200 cursor-pointer"
                      required
                    />
                    <label htmlFor="acceptPrivacy" className="text-sm text-gray-700 cursor-pointer" dir={language === 'ar' ? 'rtl' : 'ltr'}>
                      {language === 'ar' ? (
                        <>
                          أوافق على{' '}
                          <button
                            type="button"
                            onClick={() => navigate(`/${lang || 'ar'}/privacy-policy`)}
                            className="text-teal-600 hover:text-teal-700 underline font-semibold"
                          >
                            سياسة الخصوصية
                          </button>
                        </>
                      ) : (
                        <>
                          I agree to the{' '}
                          <button
                            type="button"
                            onClick={() => navigate(`/${lang || 'en'}/privacy-policy`)}
                            className="text-teal-600 hover:text-teal-700 underline font-semibold"
                          >
                            Privacy Policy
                          </button>
                        </>
                      )}
                    </label>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 py-6 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      {language === 'ar' ? 'جاري الإرسال...' : t.contact.form.sending || 'Sending...'}
                    </div>
                  ) : (
                    t.contact.form.submit
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Contact;